import React, { useEffect, useState, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ExternalLink, Play } from "lucide-react";

const ytId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};
const ytThumb = (url) => {
  const id = ytId(url);
  return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
};

const FILTERS = ["All", "Music Videos", "Films", "Advertisements", "Fashion", "Digital", "Productions"];

export default function Work() {
  const [works, setWorks] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { api.get("/works").then((r) => setWorks(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    if (filter === "All") return works;
    const map = { "Music Videos": "Music Video", "Films": "Film", "Advertisements": "Advertisement",
                  "Fashion": "Fashion", "Digital": "Digital", "Productions": "Production" };
    return works.filter((w) => (w.category || "").toLowerCase() === (map[filter] || filter).toLowerCase());
  }, [works, filter]);

  return (
    <div className="min-h-screen">
      <Header />
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-20 pb-6">
        <div className="sv-overline mb-4">Selected Work</div>
        <h1 className="font-serif-hero text-4xl lg:text-7xl text-[#F4EFE4]">Music videos cast<br /> by Senty Virk.</h1>
        <p className="mt-6 max-w-2xl text-[#CDC7BA]">A curated selection of casting collaborations across Punjabi and Indian music — for artists, labels and creative teams.</p>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex gap-2 flex-wrap py-3 border-b border-[rgba(255,255,255,0.06)]">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-4 py-2 rounded-full text-[11px] font-mono-label transition ${filter === f ? "bg-[#C5A059] text-[#0A0A0B]" : "text-[#CDC7BA] hover:text-[#F4E4BC]"}`}>
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-12">
        {filtered.length === 0 ? (
          <div className="sv-card p-16 text-center">
            <div className="sv-overline">No projects</div>
            <p className="mt-3 text-sm text-[#8A8478]">No published projects in this category yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((w) => {
              const thumb = ytThumb(w.youtube_url);
              return (
                <article key={w.work_id} data-testid={`work-${w.work_id}`} className="group relative rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.06)] bg-[#0A0A0B] transition-all duration-500 hover:border-[rgba(197,160,89,0.4)] hover:shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
                  <div className="relative aspect-video overflow-hidden bg-[#131316]">
                    {thumb ? (
                      <img src={thumb} alt={w.title} loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        onError={(e) => { e.target.src = `https://img.youtube.com/vi/${ytId(w.youtube_url)}/hqdefault.jpg`; }} />
                    ) : <div className="absolute inset-0 grid place-items-center text-[#4A4842] font-mono-label text-[10px]">No Thumbnail</div>}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050506] via-[#050506]/40 to-transparent" />
                    {w.youtube_url && (
                      <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <div className="w-16 h-16 rounded-full bg-[#C5A059] grid place-items-center shadow-[0_10px_40px_rgba(197,160,89,0.4)]">
                          <Play size={22} className="text-[#0A0A0B] ml-1" fill="currentColor" />
                        </div>
                      </div>
                    )}
                    <div className="absolute top-4 left-4 font-mono-label text-[10px] text-[#C5A059] bg-[#050506]/60 px-2 py-1 rounded backdrop-blur">{w.category || "Music Video"}</div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-editorial text-2xl text-[#F4EFE4] leading-tight transition-colors group-hover:text-[#F4E4BC]">{w.title}</h3>
                    {w.artist && <div className="mt-1 text-sm text-[#CDC7BA]">{w.artist}</div>}
                    <dl className="mt-4 space-y-1 text-[11px] text-[#8A8478]">
                      {w.music && <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Music</dt><dd className="inline">{w.music}</dd></div>}
                      {w.lyrics && <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Lyrics</dt><dd className="inline">{w.lyrics}</dd></div>}
                      {w.album && <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Album</dt><dd className="inline">{w.album}</dd></div>}
                      {w.production && <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Production</dt><dd className="inline">{w.production}</dd></div>}
                      {w.year && <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Year</dt><dd className="inline">{w.year}</dd></div>}
                      <div><dt className="inline font-mono-label mr-2 text-[#C5A059]">Role</dt><dd className="inline">{w.my_role || "Casting"} · {w.casting_director || "Senty Virk"}</dd></div>
                    </dl>
                    <div className="mt-6">
                      {w.youtube_url ? (
                        <a data-testid={`watch-${w.work_id}`} href={w.youtube_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[11px] font-mono-label text-[#C5A059] hover:text-[#F4E4BC] transition-colors">
                          Watch Project <ExternalLink size={12} />
                        </a>
                      ) : (
                        <span className="font-mono-label text-[10px] text-[#4A4842]">Watch link · Coming soon</span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
